import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import SignaturePad from "./SignaturePad";

import { tr, lbl } from "../lib/i18n";
export const FORM_LABELS = { authorization: tr("x.sale_authorization"), inspection: tr("x.inspection_release"), testdrive: tr("x.test_drive") };

const FIELDS = {
  authorization: [
    ["asking_price", tr("x.agreed_asking_price"), "number"], ["minimum_price", tr("x.minimum_price"), "number"],
    ["commission_type", tr("x.commission_type"), "select:Percentage,Fixed"], ["commission_value", tr("x.commission_value"), "number"],
    ["validity_end", tr("x.valid_until"), "date"], ["notes", tr("x.notes"), "text"],
  ],
  inspection: [
    ["destination", tr("x.workshop_destination"), "text"], ["purpose", tr("x.purpose"), "text"],
    ["odometer_out", tr("x.odometer_out_km"), "number"], ["fuel_out", tr("x.fuel_level_out"), "text"],
    ["expected_return_at", tr("x.expected_return"), "datetime-local"], ["notes", tr("x.notes"), "text"],
  ],
  testdrive: [
    ["driver_license_no", tr("x.driving_licence_no"), "text"], ["route", tr("x.route"), "text"],
    ["odometer_out", tr("x.odometer_out_km"), "number"], ["expected_return_at", tr("x.expected_return"), "datetime-local"],
    ["notes", tr("x.notes"), "text"],
  ],
};
const SIGS = {
  authorization: [["customer_signature", tr("x.customer_owner_signature")], ["employee_signature", tr("x.showroom_employee_signature")]],
  inspection: [["customer_signature", tr("x.customer_signature_release")], ["employee_signature", tr("x.employee_signature_release")]],
  testdrive: [["customer_signature", tr("x.customer_signature")]],
};

export function Field({ def, value, onChange }) {
  const [key, label, type] = def;
  if (type.startsWith("select:")) {
    return (
      <label className="grid gap-1"><span className="kpi-label">{label}</span>
        <select className="input" value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={`form-field-${key}`}>
          <option value="">—</option>{type.slice(7).split(",").map((o) => <option key={o}>{lbl(o)}</option>)}
        </select>
      </label>
    );
  }
  return (
    <label className="grid gap-1"><span className="kpi-label">{label}</span>
      <input className="input" type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={`form-field-${key}`} />
    </label>
  );
}

function useOptions() {
  const [vehicles, setVehicles] = useState([]);
  const [customers, setCustomers] = useState([]);
  useEffect(() => {
    api.get("/vehicles", { params: { limit: 200 } }).then(({ data }) => setVehicles(data.items || [])).catch(() => {});
    api.get("/customers", { params: { limit: 200 } }).then(({ data }) => setCustomers(data.items || [])).catch(() => {});
  }, []);
  return { vehicles, customers };
}

export default function NewFormModal({ onClose, onSaved, presetVehicleId }) {
  const { vehicles, customers } = useOptions();
  const [type, setType] = useState("authorization");
  const [vehicleId, setVehicleId] = useState(presetVehicleId || "");
  const [customerId, setCustomerId] = useState("");
  const [data, setData] = useState({});
  const [sigs, setSigs] = useState({});
  const [busy, setBusy] = useState(false);

  const missingSig = SIGS[type].some(([k]) => !sigs[k]);
  const submit = async () => {
    if (!vehicleId) return toast.error(tr("x.choose_a_vehicle"));
    if (!customerId) return toast.error(type === "authorization" ? tr("x.choose_the_vehicle_owner") : tr("x.choose_the_customer"));
    if (missingSig) return toast.error(tr("x.all_signatures_are_required"));
    setBusy(true);
    try {
      const { data: f } = await api.post(`/forms/${type}`, { vehicle_id: vehicleId, customer_id: customerId || null, data, ...sigs });
      toast.success(`${FORM_LABELS[type]} ${f.reference} saved`);
      onSaved(f);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start sm:items-center justify-center p-2 sm:p-4 overflow-y-auto" onClick={onClose}>
      <div className="panel w-full max-w-2xl my-4" onClick={(e) => e.stopPropagation()} data-testid="new-form-modal">
        <div className="panel-title flex items-center justify-between"><div className="kicker">{tr("x.new_form")}</div>
          <button className="btn-ghost" onClick={onClose} data-testid="new-form-close">{tr("x.close")}</button></div>
        <div className="panel-body grid gap-4">
          <div className="seg" style={{ width: "fit-content", maxWidth: "100%", overflowX: "auto" }}>
            {Object.entries(FORM_LABELS).map(([k, l]) => (
              <button key={k} className={type === k ? "active" : ""} onClick={() => { setType(k); setSigs({}); setData({}); }} data-testid={`form-type-${k}`}>{l}</button>
            ))}
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="grid gap-1"><span className="kpi-label">{tr("x.vehicle_2")}</span>
              <select className="input" value={vehicleId} onChange={(e) => setVehicleId(e.target.value)} data-testid="form-vehicle">
                <option value="">{tr("x.choose")}</option>
                {vehicles.filter((v) => v.status !== "Sold").map((v) => <option key={v.id} value={v.id}>{v.stock_number} · {v.make} {v.model} {v.model_year} · {v.status}</option>)}
              </select>
            </label>
            <label className="grid gap-1"><span className="kpi-label">{type === "authorization" ? tr("x.vehicle_owner") : tr("x.customer")}</span>
              <select className="input" value={customerId} onChange={(e) => setCustomerId(e.target.value)} data-testid="form-customer">
                <option value="">{tr("x.choose")}</option>
                {customers.map((c) => <option key={c.id} value={c.id}>{c.name} · {c.mobile || c.phone || ""}</option>)}
              </select>
            </label>
            {FIELDS[type].map((def) => <Field key={def[0]} def={def} value={data[def[0]]} onChange={(v) => setData((d) => ({ ...d, [def[0]]: v }))} />)}
          </div>
          <div className="grid gap-4 sm:grid-cols-2" key={type}>
            {SIGS[type].map(([k, l]) => <SignaturePad key={k} label={l} testid={`sig-${k}`} onChange={(v) => setSigs((s) => ({ ...s, [k]: v }))} />)}
          </div>
          <div className="flex justify-end gap-2">
            <button className="btn-secondary" onClick={onClose} data-testid="new-form-cancel">{tr("x.cancel")}</button>
            <button className="btn-primary" onClick={submit} disabled={busy} data-testid="new-form-submit">{busy ? tr("x.saving") : tr("x.save_sign")}</button>
          </div>
        </div>
      </div>
    </div>
  );
}
